import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import {
  companyTypeOptions,
  crcStatusOptions,
  groupingByOptions,
  registrationStatusOptions,
} from './company-report-options';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchCities } from '../../hooks/fetch/use-fetch-cities';
import { useFetchCompanies } from '../../hooks/fetch/use-fetch-companies';
import { useFetchPersonTypes } from '../../hooks/fetch/use-fetch-person-types';
import { useFetchCompanyTypes } from '../../hooks/fetch/use-fetch-company-types';

import Loading from '../../../../layouts/Loading';

import { GridItem } from '../../components/GridItem';
import { ReportSelect } from '../../components/ReportSelect';
import { ReportRadioGroup } from '../../components/ReportRadioGroup';
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportTogglerGroup } from '../../components/ReportTogglerGroup';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';

const CompaniesReport = () => {
  const { handleRadioChange, handleToggleChange, handleSelectChange } = useStipReport();
  const { values, setFieldValue } = useFormikContext();

  const { loading: loadingCompanies, companies } = useFetchCompanies({boWithUsers: true});
  const { loading: loadingCompanyTypes, companyTypes } = useFetchCompanyTypes();
  const { loading: loadingPersonTypes, personTypes } = useFetchPersonTypes();
  const { loading: loadingCities, cities } = useFetchCities();

  const loading = loadingCompanyTypes || loadingPersonTypes || loadingCities || loadingCompanies;
  const enableCompanies = values['tipoCadastro'] !== 1;
  const enablePerson = values['tipoCadastro'] !== 2;

  useEffect(() => {
    if (!personTypes.length || !companyTypes.length) return;
    setFieldValue('tiposEmpresa', []);
    setFieldValue('tiposPessoaFisica', []);
  }, [companyTypes, personTypes, setFieldValue, values.tipoCadastro]);

  if (loading) {
    return <Loading />;
  }

  return (
    <GridItem container spacing={3}>
      <GridItem>
        <ReportRadioGroup
          label="Tipo de cadastro:"
          options={companyTypeOptions}
          name={'tipoCadastro'}
          onSelect={handleRadioChange}
        />
      </GridItem>

      {enableCompanies && (
        <GridItem sm={6}>
          <>
            <ReportSectionTitle gutterBottom>Modalidade PJ:</ReportSectionTitle>
            <ReportMultiSelect
              options={companyTypes}
              onSelect={handleSelectChange}
              label="Selecione modalidades PJ"
              name="tiposEmpresa"
              valueProperty="cdTipoEmpresa"
              labelProperty="deTipoEmpresa"
            />
          </>
        </GridItem>
      )}

      {enablePerson && (
        <GridItem sm={6}>
          <>
            <ReportSectionTitle gutterBottom>Modalidade PF:</ReportSectionTitle>
            <ReportMultiSelect
              options={personTypes}
              onSelect={handleSelectChange}
              label="Selecione modalidades PF"
              name="tiposPessoaFisica"
              valueProperty="cdTipoPessoaFisica"
              labelProperty="deTipoPessoaFisica"
            />
          </>
        </GridItem>
      )}

      <GridItem>
        <ReportSectionTitle divider>Selecione o(s) operador(es):</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportMultiAutocomplete
          options={companies}
          onSelect={handleSelectChange}
          label="Todos os operadores"
          name="usuarios"
          valueProperty="deCpfCnpj"
          labelProperty="deRazaoSocial"
          getOptionLabel={option =>
            Boolean(option.deRazaoSocial) ? option.deRazaoSocial : option.nmCompleto
          }
          limitTags={1}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Selecione o(s) município(s):</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportMultiAutocomplete
          options={cities}
          onSelect={handleSelectChange}
          label="Todos os municípios"
          name="municipios"
          valueProperty="cdMunicipio"
          labelProperty="nmMunicipio"
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Filtrar CRC por data de emissão:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportDatePicker name="dtEmissao.dtInicio" label="Início" />
      </GridItem>
      <GridItem sm={6}>
        <ReportDatePicker name="dtEmissao.dtFim" label="Fim" />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Filtrar CRC por data de vencimento:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportDatePicker name="dtVencimento.dtInicio" label="Início" />
      </GridItem>
      <GridItem sm={6}>
        <ReportDatePicker name="dtVencimento.dtFim" label="Fim" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
      </GridItem>
      <GridItem>
        <ReportRadioGroup
          label="Situação do cadastro:"
          options={registrationStatusOptions}
          name={'situacaoCadastro'}
          onSelect={handleRadioChange}
        />
      </GridItem>
      <GridItem>
        <ReportRadioGroup
          label="Situação do CRC:"
          options={crcStatusOptions}
          name={'situacaoCrc'}
          onSelect={handleRadioChange}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Opções de agrupamento:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportSelect
          options={groupingByOptions}
          name="agrupamento"
          onSelect={handleSelectChange}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Outras opções:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportTogglerGroup
          options={[{ label: 'Exibir resumo', name: 'boExibirResumo' }]}
          onToggle={handleToggleChange}
        />
      </GridItem>
    </GridItem>
  );
};


export default CompaniesReport;
